/**
 * Frontend Debug Logger with Module-based Control
 * Provides granular control over console logging for different modules
 */

const DEBUG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

const LEVEL_NAMES = ['DEBUG', 'INFO', 'WARN', 'ERROR'];

class DebugLogger {
  constructor() {
    this.modules = new Map();
    this.globalLevel = DEBUG_LEVELS.INFO;
    // In-memory only (not persisted): pending setTimeout handles for each
    // module's auto-disable timer, keyed by module name.
    this.moduleTimeoutHandles = new Map();
    this.loadSettings();
    this.setupGlobalInterface();
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    try {
      const saved = localStorage.getItem('frontendDebugSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.globalLevel = settings.globalLevel || DEBUG_LEVELS.INFO;

        // Load module settings
        Object.entries(settings.modules || {}).forEach(([moduleName, config]) => {
          this.modules.set(moduleName, {
            enabled: config.enabled !== false,
            level: config.level || DEBUG_LEVELS.INFO,
            description: config.description || '',
            timeout_minutes: config.timeout_minutes || 0
          });
        });
      }
    } catch (error) {
      console.error('Failed to load debug settings:', error);
    }
  }

  /**
   * Save settings to localStorage
   */
  saveSettings() {
    try {
      const settings = {
        globalLevel: this.globalLevel,
        modules: Object.fromEntries(this.modules)
      };
      localStorage.setItem('frontendDebugSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save debug settings:', error);
    }
  }

  /**
   * Register a module for debugging
   */
  registerModule(moduleName, description = '', defaultEnabled = false, defaultLevel = DEBUG_LEVELS.INFO) {
    if (!this.modules.has(moduleName)) {
      this.modules.set(moduleName, {
        enabled: defaultEnabled,
        level: defaultLevel,
        description: description,
        timeout_minutes: 0
      });
      this.saveSettings();
    }
    return this.createModuleLogger(moduleName);
  }

  /**
   * Create a logger instance for a specific module
   */
  createModuleLogger(moduleName) {
    return {
      debug: (...args) => this.log(moduleName, DEBUG_LEVELS.DEBUG, ...args),
      info: (...args) => this.log(moduleName, DEBUG_LEVELS.INFO, ...args),
      warn: (...args) => this.log(moduleName, DEBUG_LEVELS.WARN, ...args),
      error: (...args) => this.log(moduleName, DEBUG_LEVELS.ERROR, ...args),
      log: (...args) => this.log(moduleName, DEBUG_LEVELS.INFO, ...args)
    };
  }

  /**
   * Main logging function
   */
  log(moduleName, level, ...args) {
    const moduleConfig = this.modules.get(moduleName);

    // Check if module is enabled and level is sufficient
    if (!moduleConfig || !moduleConfig.enabled || level < moduleConfig.level) {
      return;
    }

    // Format the log message
    const timestamp = new Date().toLocaleTimeString();
    const levelName = LEVEL_NAMES[level];
    const prefix = `[${timestamp}] [${moduleName}] [${levelName}]`;

    // Use appropriate console method
    switch (level) {
      case DEBUG_LEVELS.DEBUG:
        console.log(`%c${prefix}`, 'color: #666', ...args);
        break;
      case DEBUG_LEVELS.INFO:
        console.info(`%c${prefix}`, 'color: #0066cc', ...args);
        break;
      case DEBUG_LEVELS.WARN:
        console.warn(`%c${prefix}`, 'color: #ff8800', ...args);
        break;
      case DEBUG_LEVELS.ERROR:
        console.error(`%c${prefix}`, 'color: #cc0000', ...args);
        break;
      default:
        console.log(`%c${prefix}`, 'color: #666', ...args);
        break;
    }
  }

  /**
   * Enable debugging for a module
   */
  enable(moduleName) {
    if (this.modules.has(moduleName)) {
      this.modules.get(moduleName).enabled = true;
      this.saveSettings();
    }
  }

  /**
   * Disable debugging for a module
   */
  disable(moduleName) {
    if (this.modules.has(moduleName)) {
      this.modules.get(moduleName).enabled = false;
      this.saveSettings();
    }
    this.clearModuleTimeout(moduleName);
  }

  /**
   * Set (or clear) an auto-disable timeout for a module. minutes > 0
   * schedules the module to be disabled automatically after that many
   * minutes, replacing any timeout already pending for it; minutes <= 0
   * clears a pending timeout without disabling the module ("0 = no
   * timeout", matching the admin UI's own field label). The timer is
   * in-memory only, like the app's session-inactivity timer -- it doesn't
   * survive a page reload, only the timeout_minutes value shown in the UI
   * does.
   */
  setModuleTimeout(moduleName, minutes) {
    if (!this.modules.has(moduleName)) {
      return;
    }
    this.clearModuleTimeout(moduleName);
    this.modules.get(moduleName).timeout_minutes = minutes > 0 ? minutes : 0;
    this.saveSettings();

    if (minutes > 0) {
      const handle = setTimeout(() => {
        this.moduleTimeoutHandles.delete(moduleName);
        this.disable(moduleName);
      }, minutes * 60 * 1000);
      this.moduleTimeoutHandles.set(moduleName, handle);
    }
  }

  /**
   * Cancel a module's pending auto-disable timer, if any, without
   * changing its enabled/timeout_minutes state. Used internally so
   * disable() (manual or via a fired timeout) never leaves a stale
   * duplicate timer running.
   */
  clearModuleTimeout(moduleName) {
    const existingHandle = this.moduleTimeoutHandles.get(moduleName);
    if (existingHandle) {
      clearTimeout(existingHandle);
      this.moduleTimeoutHandles.delete(moduleName);
    }
  }

  /**
   * Set debug level for a module
   */
  setLevel(moduleName, level) {
    if (this.modules.has(moduleName)) {
      const levelNum = typeof level === 'string' ? DEBUG_LEVELS[level.toUpperCase()] : level;
      this.modules.get(moduleName).level = levelNum;
      this.saveSettings();
    }
  }

  /**
   * Enable all modules
   */
  enableAll() {
    this.modules.forEach((config) => {
      config.enabled = true;
    });
    this.saveSettings();
  }

  /**
   * Disable all modules
   */
  disableAll() {
    this.modules.forEach((config) => {
      config.enabled = false;
    });
    this.saveSettings();
  }

  /**
   * Set global debug level
   */
  setGlobalLevel(level) {
    const levelNum = typeof level === 'string' ? DEBUG_LEVELS[level.toUpperCase()] : level;
    this.globalLevel = levelNum;
    this.saveSettings();
  }

  /**
   * Get all modules and their configurations
   */
  getAllModules() {
    return Object.fromEntries(this.modules);
  }

  /**
   * Get current settings
   */
  getSettings() {
    return {
      globalLevel: this.globalLevel,
      modules: this.getAllModules()
    };
  }

  /**
   * Check if a module is enabled
   */
  isEnabled(moduleName) {
    const config = this.modules.get(moduleName);
    return config ? config.enabled : false;
  }

  /**
   * Apply preset configurations
   */
  applyPreset(presetName) {
    const presets = {
      networking: ['socketService', 'api', 'tickerPolling'],
      ui: ['tickerTable', 'navigation', 'modal', 'chart'],
      all: Array.from(this.modules.keys()),
      none: []
    };

    if (presets[presetName]) {
      // Disable all first
      this.disableAll();

      // Enable preset modules
      presets[presetName].forEach(moduleName => {
        this.enable(moduleName);
      });
    }
  }

  /**
   * Setup global browser console interface
   */
  setupGlobalInterface() {
    if (typeof window !== 'undefined') {
      window.debugControl = {
        enable: (moduleName) => this.enable(moduleName),
        disable: (moduleName) => this.disable(moduleName),
        setLevel: (moduleName, level) => this.setLevel(moduleName, level),
        enableAll: () => this.enableAll(),
        disableAll: () => this.disableAll(),
        setGlobalLevel: (level) => this.setGlobalLevel(level),
        list: () => console.table(this.getAllModules()),
        getSettings: () => this.getSettings(),
        isEnabled: (moduleName) => this.isEnabled(moduleName),
        preset: (presetName) => this.applyPreset(presetName),
        help: () => {
          console.log(`
Frontend Debug Control Commands:

debugControl.enable('moduleName')     - Enable debugging for a module
debugControl.disable('moduleName')    - Disable debugging for a module
debugControl.setLevel('module', 'level') - Set debug level (DEBUG/INFO/WARN/ERROR)
debugControl.enableAll()              - Enable all modules
debugControl.disableAll()             - Disable all modules
debugControl.list()                   - Show all modules and their status
debugControl.preset('networking')     - Apply preset (networking/ui/all/none)
debugControl.getSettings()            - Get current settings
debugControl.isEnabled('moduleName')  - Check if module is enabled

Example:
debugControl.enable('socketService')
debugControl.setLevel('api', 'DEBUG')
debugControl.preset('networking')
          `);
        }
      };
    }
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

// Export convenience functions
export const createLogger = (moduleName, description, defaultEnabled = false, defaultLevel = 'INFO') => {
  return debugLogger.registerModule(moduleName, description, defaultEnabled, DEBUG_LEVELS[defaultLevel.toUpperCase()]);
};

export const getDebugSettings = () => debugLogger.getSettings();
export const updateDebugSettings = (settings) => {
  Object.entries(settings.modules || {}).forEach(([moduleName, config]) => {
    if (config.hasOwnProperty('enabled')) {
      if (config.enabled) {
        debugLogger.enable(moduleName);
      } else {
        debugLogger.disable(moduleName);
      }
    }
    if (config.hasOwnProperty('level')) {
      debugLogger.setLevel(moduleName, config.level);
    }
  });
};

export const enableModule = (moduleName) => debugLogger.enable(moduleName);
export const disableModule = (moduleName) => debugLogger.disable(moduleName);
export const setModuleLevel = (moduleName, level) => debugLogger.setLevel(moduleName, level);
export const setModuleTimeout = (moduleName, minutes) => debugLogger.setModuleTimeout(moduleName, minutes);
export const enableAllModules = () => debugLogger.enableAll();
export const disableAllModules = () => debugLogger.disableAll();
export const applyPreset = (presetName) => debugLogger.applyPreset(presetName);

export default debugLogger;